import classNames from 'classnames';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGetWarehouseAddress } from '@steelbuy/data-access';
import { formatAddressLabel } from '@steelbuy/form';
import {
    QuoteEntryDetail,
    RFQDetailsHeading,
    RFQQuoteEntryMaterial,
    RFQStatus,
    Literal,
    getDimensionUnit,
    getTradeUnit,
    AddressModel,
} from '@steelbuy/ts-shared';
import { DownloadButton } from '@steelbuy/ui-domain';
import { Card, Tooltip, TooltipType } from '@steelbuy/ui-primitive';
import { entries, isRequestQuotePath } from '@steelbuy/util';
import { RFQTeaser } from '../../create-rfq/rfq-teaser/RFQTeaser';

import '../RFQCommonStyles.scss';
import './CardContentRFQDetails.scss';

export const TableRow = ({
    data,
    row,
    showDeliveryAddress,
}: {
    data: RFQQuoteEntryMaterial;
    row: RFQDetailsHeading;
    showDeliveryAddress: boolean;
}) => {
    const { t } = useTranslation(['translation', 'domainModel']);
    const hasDeliveryAddress = (data?.deliveryAddress as AddressModel)?.addressType !== undefined;
    const { data: warehouseAddresses = [] } = useGetWarehouseAddress({ enabled: !hasDeliveryAddress });
    const deliveryAddress = hasDeliveryAddress
        ? (data.deliveryAddress as AddressModel)
        : warehouseAddresses.find((a) => a.id === data.deliveryAddress);

    const formattedAddress = data?.deliveryAddress ? formatAddressLabel(deliveryAddress, t as TFunction) : '';

    return (
        <tr className={classNames('rfq-row')}>
            {entries(row).map(([key, { literalPath, valueMapper }]) => {
                let value: string | number | undefined = '';
                if (data) {
                    if (valueMapper) {
                        value = valueMapper(data);
                    } else if (data[key]) {
                        const rawValue = data[key];
                        if (literalPath) {
                            value = t(`domainModel:${String(literalPath)}.${rawValue}` as Literal);
                        } else if (typeof rawValue !== 'object') {
                            value = rawValue;
                        }
                    }
                }
                return (
                    <td key={key} className={classNames({ 'rfq-row__not-applicable': value === '' })}>
                        <span>
                            {value === ''
                                ? t('translation:application.createBulkListing.editListings.notApplicable')
                                : value}
                        </span>
                    </td>
                );
            })}
            {showDeliveryAddress && (
                <Tooltip content={formattedAddress} type={TooltipType.INFO}>
                    <td className="rfq-row__delivery-address-cell">{formattedAddress}</td>
                </Tooltip>
            )}
        </tr>
    );
};

type CardContentRFQDetailsProps = {
    quoteEntries: QuoteEntryDetail[];
    refNumber?: string;
    status?: RFQStatus;
    id?: string;
};

export const CardContentRFQDetails = ({ quoteEntries, refNumber, status, id }: CardContentRFQDetailsProps) => {
    const { t } = useTranslation(['translation', 'domainModel']);
    const { pathname } = useLocation();
    const isRequestQuote = isRequestQuotePath(pathname);
    return (
        <div className="card-content-rfq-details">
            <Card isClickable={false}>
                <div className="card-content-rfq-details__title">
                    {t('translation:application.createRFQ.rfqDetails')}
                    {refNumber && !isRequestQuote && (
                        <DownloadButton
                            downloadUrl={`/api/universal/documents/quote/${id}/download`}
                            label={t('translation:application.createRFQ.downloadRfq')}
                            disabled={status === RFQStatus.WITHDRAWN || status === RFQStatus.EXPIRED}
                            data-testid="download-rfq-button"
                            fileName={`SteelBuy-RFQ[${refNumber}].xls`}
                        />
                    )}
                </div>
                {refNumber && (
                    <div className="card-content-rfq-details__reference">
                        <div className="card-content-rfq-details__reference__label">
                            {t('translation:application.createRFQ.rfqReferenceNumber')}
                        </div>
                        {refNumber}
                    </div>
                )}
                {quoteEntries.map(({ materialType, product, fieldHeadings, materials, group: { label, value } }) => (
                    <div className="card-content-rfq-details__content" key={`rfq-details-group-${value}`}>
                        <RFQTeaser material={{ materialType, product, [label]: value }} />
                        <div className="rfq-table">
                            <div className="rfq-table-container">
                                <table className="rfq-table__table">
                                    <thead>
                                        <tr className="rfq-table__label">
                                            {entries(fieldHeadings).map(
                                                ([key, { measurementLiteral, measurementType }]) => {
                                                    let measurementText = '';
                                                    if (measurementType !== undefined) {
                                                        const literal =
                                                            measurementType === 'dimension'
                                                                ? `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                                : (`domainModel:material.tradeUnit.value.${getTradeUnit(
                                                                      {
                                                                          materialType,
                                                                          product,
                                                                      }
                                                                  )}.label` as Literal);
                                                        measurementText = ` (${t(literal)})`;
                                                    } else if (measurementLiteral) {
                                                        const literalText = t(
                                                            `domainModel:${measurementLiteral}` as Literal
                                                        );
                                                        measurementText = ` (${literalText})`;
                                                    }
                                                    return (
                                                        <th key={key}>
                                                            {t(
                                                                `translation:application.rfqHeadingLabels.${key}` as Literal
                                                            )}
                                                            {measurementText ? <span>{measurementText}</span> : null}
                                                        </th>
                                                    );
                                                }
                                            )}
                                            {isRequestQuote && (
                                                <th>{t('translation:application.rfqHeadingLabels.deliveryAddress')}</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {materials.map((data, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <TableRow
                                                data={data}
                                                row={fieldHeadings}
                                                key={data.id ? `${data.id}` : `rfq-details-group-${value}-${index}`}
                                                showDeliveryAddress={isRequestQuote}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </Card>
        </div>
    );
};
